import {
    FormEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import Button from '@/components/Button'
import kernelApi, {
    KernelChatDto,
    KernelTurnDbSourceTag,
    KernelTurnDto,
} from '@/api/kernel/KernelApi'

const STARTER_PROMPTS = [
    'Show me how many companies have more than 50 current employees.',
    'Tell me all programmers currently within HSBC.',
    'Summarise the last 5 network kit transcripts',
] as const

const MAX_PROMPT_LENGTH = 2000
const KERNEL_EXECUTE_GRAPH_QUERY_TOOL = 'execute_graph_query'
const DB_SOURCE_BADGE_CLASSES: Record<KernelTurnDbSourceTag, string> = {
    Graph: 'border-amber-300 bg-amber-100 text-amber-800',
    'Cold Storage': 'border-sky-300 bg-sky-100 text-sky-800',
    'Contxt PG': 'border-emerald-300 bg-emerald-100 text-emerald-800',
}

type JsonRecord = Record<string, unknown>

function isJsonRecord(value: unknown): value is JsonRecord {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function parseJsonValue(rawValue: string | null): unknown | null {
    if (!rawValue?.trim()) {
        return null
    }

    try {
        return JSON.parse(rawValue)
    } catch {
        return null
    }
}

function formatJson(value: unknown): string {
    if (value === null || value === undefined) {
        return ''
    }

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

export default function KernelChatStart(): React.JSX.Element {
    const inputRef = useRef<HTMLTextAreaElement | null>(null)
    const [chats, setChats] = useState<KernelChatDto[]>([])
    const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
    const [turns, setTurns] = useState<KernelTurnDto[]>([])
    const [prompt, setPrompt] = useState('')
    const [showToolCalls, setShowToolCalls] = useState(false)
    const [isLoadingChats, setIsLoadingChats] = useState(false)
    const [isLoadingChat, setIsLoadingChat] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const trimmedPrompt = prompt.trim()
    const canSend = trimmedPrompt.length > 0 && !isSending
    const selectedChat =
        chats.find((chat) => chat.id === selectedChatId) ?? null
    const visibleTurns = showToolCalls
        ? turns
        : turns.filter(
              (turn) =>
                  turn.role !== 'tool_call' && turn.role !== 'tool_result',
          )

    const focusInput = useCallback(() => {
        requestAnimationFrame(() => {
            inputRef.current?.focus()
        })
    }, [])

    const refreshChats = useCallback(
        async (preferredChatId?: string | null) => {
            const res = await kernelApi.getChats()
            setChats(res.chats)

            const availableChatIds = new Set(res.chats.map((chat) => chat.id))
            setSelectedChatId((currentChatId) => {
                if (
                    preferredChatId &&
                    availableChatIds.has(preferredChatId.trim())
                ) {
                    return preferredChatId.trim()
                }

                if (
                    currentChatId &&
                    availableChatIds.has(currentChatId.trim())
                ) {
                    return currentChatId.trim()
                }

                return res.chats[0]?.id ?? null
            })
        },
        [],
    )

    useEffect(() => {
        let active = true

        const loadChats = async () => {
            setIsLoadingChats(true)
            setError(null)

            try {
                await refreshChats()
            } catch (loadError) {
                if (!active) {
                    return
                }
                console.error('Failed to load Kernel chats', loadError)
                setError('Unable to load Kernel chat history.')
            } finally {
                if (active) {
                    setIsLoadingChats(false)
                }
            }
        }

        void loadChats()

        return () => {
            active = false
        }
    }, [refreshChats])

    useEffect(() => {
        let active = true

        const loadChat = async () => {
            if (!selectedChatId) {
                setTurns([])
                return
            }

            setIsLoadingChat(true)

            try {
                const res = await kernelApi.getChat(selectedChatId)
                if (!active) {
                    return
                }

                if (!res.chat) {
                    setTurns([])
                    await refreshChats()
                    return
                }

                setTurns(res.turns)
            } catch (loadError) {
                if (!active) {
                    return
                }
                console.error('Failed to load Kernel chat', loadError)
                setError('Unable to load this chat.')
            } finally {
                if (active) {
                    setIsLoadingChat(false)
                }
            }
        }

        void loadChat()

        return () => {
            active = false
        }
    }, [refreshChats, selectedChatId])

    const sendPrompt = useCallback(async () => {
        if (!canSend) {
            return
        }

        const message = trimmedPrompt
        setIsSending(true)
        setError(null)

        try {
            const res = await kernelApi.sendMessage({
                message,
                chatId: selectedChatId ?? undefined,
            })

            setPrompt('')
            setTurns(res.turns)
            setSelectedChatId(res.chat.id)
            await refreshChats(res.chat.id)
            focusInput()
        } catch (sendError) {
            console.error('Failed to send Kernel message', sendError)
            setError('Unable to send your message to Kernel right now.')
        } finally {
            setIsSending(false)
        }
    }, [canSend, focusInput, refreshChats, selectedChatId, trimmedPrompt])

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            void sendPrompt()
        },
        [sendPrompt],
    )

    const handleInputKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void sendPrompt()
            }
        },
        [sendPrompt],
    )

    const handleStarterPromptClick = useCallback(
        (starterPrompt: string) => {
            setPrompt(starterPrompt)
            focusInput()
        },
        [focusInput],
    )

    const handleCreateNewChat = useCallback(() => {
        setSelectedChatId(null)
        setTurns([])
        setError(null)
        focusInput()
    }, [focusInput])

    const handleSelectChat = useCallback((chatId: string) => {
        setSelectedChatId(chatId)
        setError(null)
    }, [])

    return (
        <div className="mx-auto w-full max-w-7xl px-2">
            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                <aside className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Chat History
                        </h3>
                        <button
                            type="button"
                            className="rounded-full border border-ctxt-blue px-3 py-1 text-xs font-semibold text-ctxt-blue transition-colors hover:bg-ctxt-blue hover:text-white"
                            onClick={handleCreateNewChat}
                        >
                            New Chat
                        </button>
                    </div>

                    <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                        {isLoadingChats ? (
                            <p className="text-xs text-gray-500">
                                Loading chats...
                            </p>
                        ) : chats.length === 0 ? (
                            <p className="text-xs text-gray-500">
                                No chats yet. Start a new Kernel conversation.
                            </p>
                        ) : (
                            chats.map((chat) => {
                                const isActive = selectedChatId === chat.id
                                return (
                                    <button
                                        key={chat.id}
                                        type="button"
                                        className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${
                                            isActive
                                                ? 'border-ctxt-blue bg-blue-50'
                                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                        onClick={() =>
                                            handleSelectChat(chat.id)
                                        }
                                    >
                                        <p className="truncate text-sm font-medium text-gray-900">
                                            {chat.title}
                                        </p>
                                        <p className="mt-1 text-[11px] text-gray-500">
                                            {chat.turnCount} turn
                                            {chat.turnCount === 1 ? '' : 's'}
                                        </p>
                                    </button>
                                )
                            })
                        )}
                    </div>
                </aside>

                <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-200 px-5 py-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {selectedChat?.title || 'Chat with Kernel'}
                        </h2>
                        <p className="mt-1 text-xs text-gray-500">
                            Internal admin assistant for graph and cold storage
                            queries.
                        </p>
                        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-700">
                            <input
                                type="checkbox"
                                checked={showToolCalls}
                                className="h-4 w-4 rounded border-gray-300 text-ctxt-blue focus:ring-ctxt-blue"
                                onChange={(event) =>
                                    setShowToolCalls(event.target.checked)
                                }
                            />
                            Show tool calls
                        </label>
                    </div>

                    <div className="max-h-[58vh] min-h-[58vh] overflow-y-auto px-5 py-4">
                        {isLoadingChat ? (
                            <p className="text-sm text-gray-500">
                                Loading chat...
                            </p>
                        ) : visibleTurns.length === 0 ? (
                            <div className="flex h-full flex-col items-center justify-center">
                                {turns.length === 0 ? (
                                    <>
                                        <p className="text-center text-sm text-gray-500">
                                            Start a new conversation with
                                            Kernel.
                                        </p>
                                        <div className="mt-4 flex max-w-2xl flex-wrap justify-center gap-2">
                                            {STARTER_PROMPTS.map(
                                                (starterPrompt) => (
                                                    <button
                                                        key={starterPrompt}
                                                        type="button"
                                                        className="rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50"
                                                        onClick={() =>
                                                            handleStarterPromptClick(
                                                                starterPrompt,
                                                            )
                                                        }
                                                    >
                                                        {starterPrompt}
                                                    </button>
                                                ),
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-center text-sm text-gray-500">
                                        Tool calls are hidden. Enable
                                        &ldquo;Show tool calls&rdquo; to view
                                        them.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {visibleTurns.map((turn) => {
                                    const isUser = turn.role === 'user'
                                    const isAgent = turn.role === 'agent'
                                    const isToolCall = turn.role === 'tool_call'
                                    const isToolResult =
                                        turn.role === 'tool_result'
                                    const dbSourceTags = isAgent
                                        ? turn.dbSourceTags
                                        : []

                                    if (isUser || isAgent) {
                                        return (
                                            <div
                                                key={turn.id}
                                                className={`flex ${
                                                    isUser
                                                        ? 'justify-end'
                                                        : 'justify-start'
                                                }`}
                                            >
                                                <div
                                                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                                                        isUser
                                                            ? 'bg-ctxt-blue text-white'
                                                            : 'bg-gray-100 text-gray-900'
                                                    }`}
                                                >
                                                    {turn.text ? (
                                                        <p className="whitespace-pre-wrap">
                                                            {turn.text}
                                                        </p>
                                                    ) : (
                                                        <p className="italic opacity-80">
                                                            [No text]
                                                        </p>
                                                    )}
                                                    {dbSourceTags.length > 0 ? (
                                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                                            {dbSourceTags.map(
                                                                (tag) => (
                                                                    <span
                                                                        key={`${turn.id}-${tag}`}
                                                                        className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${DB_SOURCE_BADGE_CLASSES[tag]}`}
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                ),
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        )
                                    }

                                    const toolArgs = parseJsonValue(
                                        turn.toolArgumentsJson,
                                    )
                                    const toolResult = parseJsonValue(
                                        turn.toolResultJson,
                                    )
                                    const toolName =
                                        turn.toolName?.trim() || 'Unknown Tool'
                                    const isGraphQueryCall =
                                        isToolCall &&
                                        turn.toolName ===
                                            KERNEL_EXECUTE_GRAPH_QUERY_TOOL &&
                                        isJsonRecord(toolArgs)
                                    const cypherQuery = isGraphQueryCall
                                        ? toolArgs.query
                                        : null
                                    const cypherParams = isGraphQueryCall
                                        ? toolArgs.params
                                        : null
                                    const cypherQueryText =
                                        typeof cypherQuery === 'string'
                                            ? cypherQuery
                                            : null
                                    const hasGraphQuery =
                                        typeof cypherQueryText === 'string' &&
                                        cypherQueryText.trim().length > 0
                                    let toolContainerClasses =
                                        'border-emerald-200 bg-emerald-50 text-emerald-950'
                                    if (isToolCall) {
                                        toolContainerClasses =
                                            'border-blue-200 bg-blue-50 text-blue-950'
                                    }
                                    if (isToolResult && turn.isError) {
                                        toolContainerClasses =
                                            'border-red-200 bg-red-50 text-red-950'
                                    }

                                    return (
                                        <div
                                            key={turn.id}
                                            className="flex justify-start"
                                        >
                                            <div
                                                className={`max-w-[90%] rounded-2xl border px-4 py-3 text-sm leading-relaxed ${toolContainerClasses}`}
                                            >
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold tracking-wide">
                                                    <span>
                                                        {isToolCall
                                                            ? 'TOOL CALL'
                                                            : 'TOOL RESULT'}
                                                    </span>
                                                    <span className="rounded-full bg-white/70 px-2 py-0.5 font-medium">
                                                        {toolName}
                                                    </span>
                                                    {turn.toolCallId ? (
                                                        <span className="font-mono text-[11px] opacity-70">
                                                            {turn.toolCallId}
                                                        </span>
                                                    ) : null}
                                                    {isToolResult ? (
                                                        <span
                                                            className={`rounded-full px-2 py-0.5 text-[11px] ${
                                                                turn.isError
                                                                    ? 'bg-red-100 text-red-700'
                                                                    : 'bg-emerald-100 text-emerald-700'
                                                            }`}
                                                        >
                                                            {turn.isError
                                                                ? 'error'
                                                                : 'ok'}
                                                        </span>
                                                    ) : null}
                                                </div>

                                                {turn.text ? (
                                                    <p className="mt-2 whitespace-pre-wrap text-sm">
                                                        {turn.text}
                                                    </p>
                                                ) : null}

                                                {hasGraphQuery ? (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                                                            Cypher Query
                                                        </p>
                                                        <pre className="mt-1 overflow-x-auto rounded-lg bg-slate-900/90 px-3 py-2 text-xs text-slate-100">
                                                            {cypherQueryText}
                                                        </pre>
                                                    </div>
                                                ) : null}

                                                {isToolCall &&
                                                !hasGraphQuery &&
                                                toolArgs !== null ? (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                                                            Arguments
                                                        </p>
                                                        <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-white/80 px-3 py-2 font-mono text-xs text-slate-800">
                                                            {formatJson(
                                                                toolArgs,
                                                            ) ||
                                                                '[No arguments]'}
                                                        </pre>
                                                    </div>
                                                ) : null}

                                                {hasGraphQuery &&
                                                cypherParams !== null &&
                                                cypherParams !== undefined ? (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                                                            Query Params
                                                        </p>
                                                        <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-white/80 px-3 py-2 font-mono text-xs text-slate-800">
                                                            {formatJson(
                                                                cypherParams,
                                                            ) || '[No params]'}
                                                        </pre>
                                                    </div>
                                                ) : null}

                                                {isToolResult &&
                                                toolResult !== null ? (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                                                            Result
                                                        </p>
                                                        <pre className="mt-1 max-h-72 overflow-auto rounded-lg bg-white/80 px-3 py-2 font-mono text-xs text-slate-800">
                                                            {formatJson(
                                                                toolResult,
                                                            ) ||
                                                                '[No result payload]'}
                                                        </pre>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        className="border-t border-gray-200 px-5 py-4"
                    >
                        {error ? (
                            <p className="mb-3 text-sm text-red-600">{error}</p>
                        ) : null}
                        <div className="overflow-hidden rounded-3xl border border-gray-300 bg-white">
                            <textarea
                                ref={inputRef}
                                value={prompt}
                                maxLength={MAX_PROMPT_LENGTH}
                                rows={4}
                                placeholder="Ask Kernel about graph or cold storage data..."
                                className="w-full resize-none border-0 px-5 py-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                                onChange={(event) =>
                                    setPrompt(event.target.value)
                                }
                                onKeyDown={handleInputKeyDown}
                            />
                            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3">
                                <p className="text-xs text-gray-500">
                                    Press Enter to send, Shift+Enter for a new
                                    line.
                                </p>
                                <Button
                                    type="submit"
                                    style="primary"
                                    enabled={canSend}
                                >
                                    {isSending ? 'Sending...' : 'Ask Kernel'}
                                </Button>
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </div>
    )
}
